import { useState } from 'react';
import {
  Table, Button, Space, Tag, Modal, Form, Input, message,
  Popconfirm, Card, Select, Alert, Typography
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, CopyOutlined, ReloadOutlined,
  KeyOutlined, CheckCircleOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiKeysApi, type ApiKeyDto, type ApiKeyCreatedResponse } from '../api/apiKeys';
import { firmsApi } from '../api/firms';
import type { Firm } from '../types';

const { Text, Paragraph } = Typography;

export function ApiKeys() {
  const [selectedFirmId, setSelectedFirmId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newKey, setNewKey] = useState<ApiKeyCreatedResponse | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const { data: firms = [] } = useQuery({
    queryKey: ['firms'],
    queryFn: firmsApi.getAll,
  });

  const { data: apiKeys = [], isLoading } = useQuery({
    queryKey: ['api-keys', selectedFirmId],
    queryFn: () => selectedFirmId ? apiKeysApi.getByFirm(selectedFirmId) : Promise.resolve([]),
    enabled: !!selectedFirmId,
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string }) =>
      apiKeysApi.create(selectedFirmId!, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setNewKey(response);
      form.resetFields();
    },
    onError: () => message.error('API key oluşturulamadı'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiKeysApi.delete(selectedFirmId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      message.success('API key silindi');
    },
    onError: () => message.error('API key silinemedi'),
  });

  const regenerateMutation = useMutation({
    mutationFn: (id: number) => apiKeysApi.regenerate(selectedFirmId!, id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      setNewKey(response);
      message.success('API key yenilendi');
    },
    onError: () => message.error('API key yenilenemedi'),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      apiKeysApi.update(selectedFirmId!, id, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      message.success('API key güncellendi');
    },
    onError: () => message.error('API key güncellenemedi'),
  });

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Kopyalandı');
  };

  const columns = [
    {
      title: 'Ad',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => (
        <Space>
          <KeyOutlined />
          {name}
        </Space>
      ),
    },
    {
      title: 'API Key',
      dataIndex: 'keyPreview',
      key: 'keyPreview',
      render: (preview: string) => <code>{preview}</code>,
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean, record: ApiKeyDto) => (
        <Tag
          color={isActive ? 'green' : 'default'}
          style={{ cursor: 'pointer' }}
          onClick={() => toggleActiveMutation.mutate({ id: record.id, isActive: !isActive })}
        >
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      ),
    },
    {
      title: 'Son Kullanım',
      dataIndex: 'lastUsedAt',
      key: 'lastUsedAt',
      render: (date: string | null) =>
        date ? new Date(date).toLocaleString('tr-TR') : 'Hiç kullanılmadı',
    },
    {
      title: 'Oluşturulma',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR'),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: unknown, record: ApiKeyDto) => (
        <Space>
          <Popconfirm
            title="API key'i yenilemek istediğinize emin misiniz?"
            description="Eski key artık çalışmayacak!"
            onConfirm={() => regenerateMutation.mutate(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="text" icon={<ReloadOutlined />} title="Yenile" />
          </Popconfirm>
          <Popconfirm
            title="API key'i silmek istediğinize emin misiniz?"
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2>API Keys</h2>
        <Space>
          <Select
            style={{ width: 300 }}
            placeholder="Firma seçin"
            value={selectedFirmId}
            onChange={setSelectedFirmId}
            options={firms.map((f: Firm) => ({ value: f.id, label: f.name }))}
            allowClear
          />
          {selectedFirmId && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setNewKey(null);
                form.resetFields();
                setIsModalOpen(true);
              }}
            >
              Yeni API Key
            </Button>
          )}
        </Space>
      </div>

      {selectedFirmId ? (
        <>
          <Alert
            message="API Key Kullanımı"
            description={
              <span>
                API isteklerinde <code>X-Api-Key</code> header'ı ile key'i gönderin.
                Örnek: <code>curl -H "X-Api-Key: rg_api_xxx" {window.location.origin}/api/v1/sources</code>
              </span>
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Table
            columns={columns}
            dataSource={apiKeys}
            rowKey="id"
            loading={isLoading}
            locale={{ emptyText: 'API key bulunamadı' }}
          />
        </>
      ) : (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#666' }}>
            API key'leri görmek için yukarıdan bir firma seçin.
          </div>
        </Card>
      )}

      {/* Create Modal */}
      <Modal
        title={newKey ? 'API Key Oluşturuldu' : 'Yeni API Key'}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setNewKey(null);
          form.resetFields();
        }}
        footer={newKey ? [
          <Button key="close" type="primary" onClick={() => {
            setIsModalOpen(false);
            setNewKey(null);
          }}>
            Tamam
          </Button>
        ] : null}
        width={600}
      >
        {newKey ? (
          <div>
            <Alert
              message="API Key başarıyla oluşturuldu!"
              description="Bu key sadece bir kez gösterilecek. Lütfen güvenli bir yerde saklayın."
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              style={{ marginBottom: 16 }}
            />

            <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8 }}>
              <Text type="secondary">API Key:</Text>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <Paragraph
                  copyable
                  code
                  style={{ margin: 0, flex: 1, fontSize: 14 }}
                >
                  {newKey.apiKey}
                </Paragraph>
              </div>
            </div>

            <div style={{ marginTop: 16, padding: 16, background: '#f0f5ff', borderRadius: 8 }}>
              <Text strong>Örnek Kullanım:</Text>
              <pre style={{ marginTop: 8, fontSize: 12, overflow: 'auto' }}>
{`curl -H "X-Api-Key: ${newKey.apiKey}" \\
  ${window.location.origin}/api/v1/sources`}
              </pre>
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(`curl -H "X-Api-Key: ${newKey.apiKey}" ${window.location.origin}/api/v1/sources`)}
              >
                Kopyala
              </Button>
            </div>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => createMutation.mutate(values)}
          >
            <Form.Item
              name="name"
              label="API Key Adı"
              rules={[{ required: true, message: 'Ad gerekli' }]}
              extra="Bu API key'in ne için kullanılacağını açıklayan bir ad"
            >
              <Input placeholder="Örn: Dashboard Entegrasyonu" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={createMutation.isPending}
                >
                  Oluştur
                </Button>
                <Button onClick={() => setIsModalOpen(false)}>
                  İptal
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
}
